import type { MouseEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import {
  $applyNodeReplacement,
  $getNodeByKey,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  DecoratorNode,
  DOMConversionMap,
  DOMExportOutput,
  DRAGSTART_COMMAND,
  EditorConfig,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

import { DownloadOutline, FileTextFill } from '@lyearn/icons';
import { Stack, Typography } from '@lyearn/ui';

export type SerializedFileNode = Spread<
  {
    url: string;
    filename: string;
    fileSize: string;
    type: 'file';
    version: 1;
  },
  SerializedLexicalNode
>;

export class FileNode extends DecoratorNode<JSX.Element> {
  __url: string;
  __filename: string;
  __fileSize: string;

  static getType(): string {
    return 'file';
  }

  static clone(node: FileNode): FileNode {
    return new FileNode(node.__url, node.__filename, node.__fileSize, node.__key);
  }

  constructor(url: string, filename: string, fileSize: string, key?: NodeKey) {
    super(key);
    this.__url = url;
    this.__filename = filename;
    this.__fileSize = fileSize;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const div = document.createElement('div');
    const theme = config.theme;
    const className = theme.file;
    if (className) {
      div.className = className;
    }
    return div;
  }

  updateDOM(): false {
    return false;
  }

  static importDOM(): DOMConversionMap | null {
    return null;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('a');
    element.href = this.__url;
    element.download = this.__filename;
    element.textContent = this.__filename;
    return { element };
  }

  decorate(): JSX.Element {
    return (
      <FileComponent
        fileSize={this.__fileSize}
        filename={this.__filename}
        nodeKey={this.getKey()}
        url={this.__url}
      />
    );
  }

  static importJSON(serializedNode: SerializedFileNode): FileNode {
    const node = $createFileNode(
      serializedNode.url,
      serializedNode.filename,
      serializedNode.fileSize,
    );
    return node;
  }

  exportJSON(): SerializedFileNode {
    return {
      ...super.exportJSON(),
      url: this.__url,
      filename: this.__filename,
      fileSize: this.__fileSize,
      type: 'file',
      version: 1,
    };
  }
}

export function $createFileNode(url: string, filename: string, fileSize: string): FileNode {
  return $applyNodeReplacement(new FileNode(url, filename, fileSize));
}

function FileComponent({
  url,
  filename,
  fileSize,
  nodeKey,
}: {
  url: string;
  filename: string;
  fileSize: string;
  nodeKey: string;
}) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const fileRef = useRef<HTMLDivElement>(null);

  const onDelete = useCallback(
    (event: KeyboardEvent) => {
      if (isSelected) {
        event.preventDefault();
        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          if (node) {
            node.remove();
          }
        });
        return true;
      }
      return false;
    },
    [editor, isSelected, nodeKey],
  );

  const onClick = useCallback(
    (event: MouseEvent) => {
      if (event.target === fileRef.current || fileRef.current?.contains(event.target as Node)) {
        event.preventDefault();
        if (event.shiftKey) {
          setSelected(!isSelected);
        } else {
          clearSelection();
          setSelected(true);
        }
        return true;
      }
      return false;
    },
    [clearSelection, setSelected, isSelected],
  );

  useEffect(() => {
    const unregister = mergeRegister(
      editor.registerCommand(CLICK_COMMAND, onClick, COMMAND_PRIORITY_LOW),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          if (event.target === fileRef.current) {
            event.preventDefault();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
    );

    return unregister;
  }, [editor, onClick, onDelete]);

  return (
    <div className="relative w-full my-8 flex justify-center">
      <div
        ref={fileRef}
        aria-label="File attachment"
        className={`w-[60%] max-w-3xl relative cursor-pointer ${
          isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : ''
        }`}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClick(e as unknown as MouseEvent);
          }
        }}>
        <Stack
          alignItems="center"
          className="h-64 shadow-02 hover:shadow-03 px-16 py-8 bg-interactive-primary-active rounded-lg rounded-6"
          direction="row"
          gap={16}>
          <FileTextFill className="w-20 h-20 shrink-0" fillColorToken="icon-white" />
          <Stack className="w-full" direction="col">
            <Typography
              className="text-bg-primary font-medium w-full"
              numberOfLines={1}
              variant="body-short-01">
              {filename}
            </Typography>
            <Typography
              className="text-bg-primary text-sm text-gray-500"
              numberOfLines={1}
              variant="body-short-02">
              {fileSize}
            </Typography>
          </Stack>
          <a
            className="p-2 hover:bg-gray-100 rounded-full shrink-0"
            download={filename}
            href={url}
            rel="noopener noreferrer"
            target="_blank"
            onClick={(e) => e.stopPropagation()}>
            <DownloadOutline className="w-5 h-5 text-gray-600" fillColorToken="icon-white" />
          </a>
        </Stack>
      </div>
    </div>
  );
}
